import {
    Autocomplete,
    Box,
    Button,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    FormHelperText,
    MenuItem,
    Select,
    TextField,
    Typography,
    styled,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { useFetch, useIsBasicRole, useSnackbar } from "../../hooks";

import { BACKEND_ROUTES } from "../../backendRoutes";
import { CountryFlag } from "../../components/CountryFlag";
import { INPUT_VALIDATION } from "../../constants";
import { LoadingButton } from "@mui/lab";
import { MODEL_PROPTYPES } from "../../models";
import PropTypes from "prop-types";
import { SORTED_COUNTRIES } from "./constants";
import { Stack } from "@mui/system";

SiteCreateForm.propTypes = {
    contract: PropTypes.shape(MODEL_PROPTYPES.Contract).isRequired,
    closeModal: PropTypes.func.isRequired,
    mutate: PropTypes.func.isRequired,
    users: PropTypes.arrayOf(PropTypes.shape(MODEL_PROPTYPES.User)).isRequired,
    hasLiteralTemplates: PropTypes.bool,
};

export function SiteCreateForm({
    contract,
    closeModal,
    mutate,
    users,
    hasLiteralTemplates,
}) {
    const { post } = useFetch();
    const isBasicRole = useIsBasicRole();
    const { openSnackbar } = useSnackbar();

    const {
        control,
        register,
        handleSubmit,
        formState: { errors, isSubmitting },
    } = useForm({
        defaultValues: {
            name: "",
            displayName: "",
            processingLeadUuid: isBasicRole ? contract.processingLeadUuid : "",
            countryCode: null,
            experimentStatus: hasLiteralTemplates ? 0 : -1,
        },
    });

    const onSubmit = async (payload) => {
        const createdSite = await post(
            `${BACKEND_ROUTES.CONTRACT}/${contract.uuid}/sites`,
            {
                body: {
                    ...payload,
                    countryCode: payload.countryCode?.alpha3 ?? null,
                    displayName: payload.displayName || undefined,
                },
            }
        );

        if (createdSite) {
            mutate();
            closeModal();
            openSnackbar(
                `Site ${createdSite.displayName} created successfully.`,
                "success"
            );
        }
    };

    const LabelStyle = styled(Typography)(({ theme }) => ({
        color: theme.palette.text.secondary,
        fontWeight: 500,
    }));

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <DialogTitle>Create a new site</DialogTitle>
            <Divider variant="middle" />
            <DialogContent>
                <Stack spacing={3}>
                    <TextField
                        id="name"
                        fullWidth
                        type="text"
                        label="Name *"
                        error={Boolean(errors.name)}
                        helperText={errors.name?.message}
                        {...register("name", {
                            required: INPUT_VALIDATION.REQUIRED,
                        })}
                    />

                    {!isBasicRole && (
                        <>
                            <TextField
                                id="displayName"
                                fullWidth
                                type="text"
                                label="Display name"
                                error={Boolean(errors.displayName)}
                                helperText={errors.displayName?.message}
                                {...register("displayName")}
                            />
                            <Controller
                                control={control}
                                name="processingLeadUuid"
                                render={({ field: { ref, ...fieldProps } }) => (
                                    <TextField
                                        {...fieldProps}
                                        fullWidth
                                        id="processingLeadUuid"
                                        select
                                        label="Processing Lead *"
                                        inputRef={ref}
                                        error={Boolean(
                                            errors.processingLeadUuid
                                        )}
                                        helperText={
                                            errors.processingLeadUuid?.message
                                        }
                                    >
                                        {users.map((user) => (
                                            <MenuItem
                                                key={user.uuid}
                                                value={user.uuid}
                                            >
                                                {`${user.firstName} ${user.lastName}`}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                )}
                                rules={{
                                    required: INPUT_VALIDATION.REQUIRED,
                                }}
                            />
                        </>
                    )}
                    <Controller
                        control={control}
                        name="countryCode"
                        render={({
                            field: { ref, onChange, ...fieldProps },
                        }) => (
                            <Autocomplete
                                {...fieldProps}
                                id="countryCode"
                                options={SORTED_COUNTRIES}
                                isOptionEqualToValue={(option, value) =>
                                    option.alpha3 === value.alpha3
                                }
                                fullWidth
                                onChange={(_, value) => {
                                    onChange(value);
                                }}
                                getOptionLabel={(option) => option.name}
                                renderOption={(props, option) => (
                                    <Box {...props} component="li">
                                        <Stack direction="row" spacing={1}>
                                            <CountryFlag country={option} />
                                            <span>{option.name}</span>
                                        </Stack>
                                    </Box>
                                )}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        inputRef={ref}
                                        label="Country"
                                    />
                                )}
                            />
                        )}
                    />
                    {!hasLiteralTemplates && !isBasicRole && (
                        <Controller
                            name="experimentStatus"
                            control={control}
                            rules={{
                                required: INPUT_VALIDATION.REQUIRED,
                            }}
                            render={({ field, fieldState }) => (
                                <FormControl
                                    fullWidth
                                    error={Boolean(fieldState.error)}
                                >
                                    <LabelStyle>
                                        Field map provider *
                                    </LabelStyle>
                                    <Select
                                        {...field}
                                        size="small"
                                        displayEmpty
                                        onChange={(event) =>
                                            field.onChange(event.target.value)
                                        }
                                        value={field.value ?? ""}
                                    >
                                        <MenuItem value={-1}>
                                            Provided by Hiphen
                                        </MenuItem>
                                        <MenuItem value={0}>
                                            Provided by the client
                                        </MenuItem>
                                    </Select>
                                    <FormHelperText>
                                        {fieldState.error?.message}
                                    </FormHelperText>
                                </FormControl>
                            )}
                        />
                    )}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button type="button" onClick={closeModal}>
                    Cancel
                </Button>
                <LoadingButton
                    type="submit"
                    variant="contained"
                    loading={isSubmitting}
                >
                    Create
                </LoadingButton>
            </DialogActions>
        </form>
    );
}
